import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { AvatarCropper } from "@/components/AvatarCropper";
import { imgSrc } from "@/lib/api";

const FIELDS = [
  ["name", "Name"], ["business", "Business name"], ["title", "Your title (e.g. Founder & CEO)"],
  ["location", "Location"], ["website", "Website"], ["instagram", "Instagram handle"],
];

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState(() => Object.fromEntries([...FIELDS.map(([k]) => [k, user[k] || ""]), ["bio", user.bio || ""], ["avatar_url", user.avatar_url || ""]]));
  const [busy, setBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image");
      return;
    }
    setCropSrc(URL.createObjectURL(file));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      setUser((await api.put("/me/profile", f)).data);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="profile-page">
      <div className="label-accent">Your seat at the table</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-1">My profile</h1>
      <form onSubmit={save} className="hb-card p-6 md:p-8 mt-8 space-y-4">
        <div className="flex items-center gap-4">
          <UserAvatar user={{ ...user, ...f }} size={72} />
          <div className="space-y-2">
            <p className="text-sm text-[var(--hb-muted)]">This is how other members see you in the directory.</p>
            <div className="flex flex-wrap gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="profile-photo-upload-input" />
              <button type="button" className="btn-ghost" onClick={() => fileRef.current?.click()} data-testid="profile-photo-upload">
                {f.avatar_url ? "Change photo" : "Upload photo"}
              </button>
              {f.avatar_url && (
                <button type="button" className="btn-ghost" onClick={() => setCropSrc(imgSrc(f.avatar_url))} data-testid="profile-photo-adjust">Adjust</button>
              )}
              {f.avatar_url && (
                <button type="button" className="btn-ghost" onClick={() => setF({ ...f, avatar_url: "" })} data-testid="profile-photo-remove">Remove</button>
              )}
            </div>
            {f.avatar_url !== (user.avatar_url || "") && <p className="text-xs text-[var(--hb-muted)]">Click Save profile to keep this photo.</p>}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, label]) => (
            <label key={k}>
              <span className="label-accent">{label}</span>
              <input className="hb-input mt-1.5" value={f[k]} required={k === "name"} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`profile-${k}-input`} />
            </label>
          ))}
        </div>
        <label className="block">
          <span className="label-accent">Bio</span>
          <textarea className="hb-input mt-1.5 min-h-[120px]" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })}
            placeholder="What you're building, who you serve, how members can support you" data-testid="profile-bio-input" />
        </label>
        <button className="btn-primary" disabled={busy} data-testid="profile-save-button">{busy ? "Saving..." : "Save profile"}</button>
      </form>
      <AvatarCropper src={cropSrc} onClose={() => setCropSrc(null)}
        onDone={(url) => { setF((prev) => ({ ...prev, avatar_url: url })); setCropSrc(null); }} />
    </div>
  );
}
